import React, { useState, useRef, useEffect, useCallback } from 'react';
import { useTheme, alpha } from '@mui/material/styles';
import { Modal, Button, Alert, Spinner, Badge } from 'react-bootstrap';
import { useTranslation } from 'react-i18next';
import * as faceapi from '@vladmandic/face-api';

interface FaceCaptureProps {
  show: boolean;
  onHide: () => void;
  onComplete: (images: Array<{ file: File; pose: string; image_rank: number }>) => void;
  startingRank?: number;
}

const POSES = [
  { labelKey: 'frontPose', pose: 'front', offset: 0 },
  { labelKey: 'leftSidePose12', pose: 'left-side', offset: 1 },
  { labelKey: 'leftSidePose22', pose: 'left-side', offset: 2 },
  { labelKey: 'rightSidePose12', pose: 'right-side', offset: 3 },
  { labelKey: 'rightSidePose22', pose: 'right-side', offset: 4 },
];

const FaceCapture: React.FC<FaceCaptureProps> = ({ show, onHide, onComplete, startingRank = 1 }) => {
  const { t } = useTranslation('components', { keyPrefix: 'faceCapture' });
  const theme = useTheme();
  const [currentStep, setCurrentStep] = useState(0);
  const [capturedImages, setCapturedImages] = useState<Array<{ file: File; pose: string; image_rank: number; preview: string }>>([]);
  const [error, setError] = useState<string | null>(null);
  const [isCameraReady, setIsCameraReady] = useState(false);
  const [currentRank, setCurrentRank] = useState(startingRank);
  const [facingMode, setFacingMode] = useState<'user' | 'environment'>('user');
  const [supportsCameraSwitch, setSupportsCameraSwitch] = useState(false);
  
  // Face detection states
  const [modelsLoaded, setModelsLoaded] = useState(false);
  const [detection, setDetection] = useState<faceapi.FaceDetection | null>(null);
  const [detectionStatus, setDetectionStatus] = useState<'none' | 'detecting' | 'valid' | 'too-small' | 'multiple'>('detecting');

  const streamRef = useRef<MediaStream | null>(null);
  const videoRef = useRef<HTMLVideoElement>(null);
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const detectionTimeoutRef = useRef<NodeJS.Timeout | null>(null);

  // Load face-api models
  useEffect(() => {
    const loadModels = async () => {
      try {
        const MODEL_URL = '/models';
        await Promise.all([
          faceapi.nets.tinyFaceDetector.loadFromUri(MODEL_URL),
          faceapi.nets.faceLandmark68Net.loadFromUri(MODEL_URL),
        ]);
        setModelsLoaded(true);
      } catch (err) {
        console.error('Error loading face-api models:', err);
        setError(t('failedLoadModels'));
      }
    };
    if (show) {
      loadModels();
    }
  }, [show, t]);

  // Run face detection loop
  const runDetection = useCallback(async () => {
    if (!videoRef.current || !modelsLoaded || !isCameraReady || currentStep >= POSES.length) {
      return;
    }

    try {
      const options = new faceapi.TinyFaceDetectorOptions({ inputSize: 224, scoreThreshold: 0.5 });
      const result = await faceapi.detectSingleFace(videoRef.current, options);

      if (result) {
        setDetection(result);
        
        const box = result.box;
        const videoHeight = videoRef.current.videoHeight;
        
        const isSizedWell = (box.height / videoHeight) > 0.25;
        
        if (isSizedWell) {
          setDetectionStatus('valid');
        } else {
          setDetectionStatus('too-small');
        }
      } else {
        setDetection(null);
        setDetectionStatus('none');
      }
    } catch (err) {
      console.error('Detection error:', err);
    }

    if (show) {
      detectionTimeoutRef.current = setTimeout(runDetection, 100);
    }
  }, [modelsLoaded, isCameraReady, show, currentStep]);

  useEffect(() => {
    if (modelsLoaded && isCameraReady) {
      runDetection();
    }
    return () => {
      if (detectionTimeoutRef.current) {
        clearTimeout(detectionTimeoutRef.current);
      }
    };
  }, [modelsLoaded, isCameraReady, runDetection]);

  useEffect(() => {
    if (currentStep < POSES.length) {
      setCurrentRank(startingRank + POSES[currentStep].offset);
    }
  }, [currentStep, startingRank]);

  const stopCamera = useCallback(() => {
    if (streamRef.current) {
      streamRef.current.getTracks().forEach(track => track.stop());
      streamRef.current = null;
    }
    setIsCameraReady(false);
  }, []);

  const loadCameraDevices = useCallback(async () => {
    try {
      if (!navigator.mediaDevices || !navigator.mediaDevices.enumerateDevices) {
        return;
      }
      const devices = await navigator.mediaDevices.enumerateDevices();
      const videoInputs = devices.filter((device) => device.kind === 'videoinput');
      setSupportsCameraSwitch(videoInputs.length > 1);
    } catch (err) {
      console.error('Error enumerating devices:', err);
      setSupportsCameraSwitch(false);
    }
  }, []);

  const startCamera = useCallback(async (mode: 'user' | 'environment' = facingMode) => {
    try {
      setError(null);
      
      if (!navigator.mediaDevices || !navigator.mediaDevices.getUserMedia) {
        if (!window.isSecureContext) {
          setError(t('cameraRequiresHttps'));
        } else {
          setError(t('browserNoCameraSupport'));
        }
        return;
      }

      stopCamera();

      const constraints: MediaStreamConstraints = {
        video: {
          facingMode: { ideal: mode },
          width: { ideal: 1280 },
          height: { ideal: 720 },
        },
      };

      const newStream = await navigator.mediaDevices.getUserMedia(constraints);
      streamRef.current = newStream;
      setFacingMode(mode);

      if (videoRef.current) {
        videoRef.current.srcObject = newStream;
        videoRef.current.onloadedmetadata = () => {
          setIsCameraReady(true);
        };
      }
    } catch (err: any) {
      console.error('Error accessing camera:', err);
      
      if (err.name === 'NotAllowedError' || err.name === 'PermissionDeniedError') {
        setError(t('cameraPermissionDenied'));
      } else if (err.name === 'NotFoundError' || err.name === 'DevicesNotFoundError') {
        setError(t('noCameraFound'));
      } else if (mode === 'environment') {
        try {
          const fallbackStream = await navigator.mediaDevices.getUserMedia({
            video: {
              facingMode: 'user',
              width: { ideal: 1280 },
              height: { ideal: 720 },
            },
          });
          streamRef.current = fallbackStream;
          setFacingMode('user');
          if (videoRef.current) {
            videoRef.current.srcObject = fallbackStream;
            videoRef.current.onloadedmetadata = () => setIsCameraReady(true);
          }
          return;
        } catch (fallbackErr) {
          console.error('Fallback to user camera failed:', fallbackErr);
          setError(t('couldNotAccessCamera'));
        }
      } else {
        setError(t('cameraError', { errorName: err.name || 'Error' }));
      }
    }
  }, [stopCamera, t]);

  const switchCamera = async () => {
    const nextMode = facingMode === 'user' ? 'environment' : 'user';
    await startCamera(nextMode);
  };

  useEffect(() => {
    if (show) {
      loadCameraDevices();
      startCamera();
    }
    return () => {
      stopCamera();
    };
  }, [show, startCamera, stopCamera, loadCameraDevices]);

  const captureFrame = () => {
    if (!videoRef.current || !canvasRef.current) return;

    const video = videoRef.current;
    const canvas = canvasRef.current;
    const context = canvas.getContext('2d');
    if (!context) return;

    canvas.width = video.videoWidth;
    canvas.height = video.videoHeight;

    context.drawImage(video, 0, 0, canvas.width, canvas.height);

    canvas.toBlob((blob) => {
      if (blob) {
        const currentPose = POSES[currentStep];
        const actualRank = currentRank;
        const file = new File([blob], `capture_${currentPose.pose}_rank${actualRank}.jpg`, { type: 'image/jpeg' });
        const preview = canvas.toDataURL('image/jpeg');
        
        const newCaptured = [...capturedImages, { file, pose: currentPose.pose, image_rank: actualRank, preview }];
        setCapturedImages(newCaptured);

        if (currentStep < POSES.length - 1) {
          setCurrentStep(currentStep + 1);
        } else {
          setCurrentStep(POSES.length);
          
          setTimeout(() => {
            const finalImages = newCaptured.map(({ file, pose, image_rank }) => ({ file, pose, image_rank }));
            console.log('FaceCapture: Auto-finishing with', finalImages.length, 'images');
            onComplete(finalImages);
            stopCamera();
          }, 800);
        }
      }
    }, 'image/jpeg', 0.9);
  };

  const handleFinish = () => {
    const finalImages = capturedImages.map(({ file, pose, image_rank }) => ({ file, pose, image_rank }));
    console.log('FaceCapture: Finishing with', finalImages.length, 'images');
    onComplete(finalImages);
    stopCamera();
  };

  const retakeLast = () => {
    if (capturedImages.length > 0) {
      setCapturedImages(capturedImages.slice(0, -1));
      setCurrentStep(currentStep - 1);
    }
  };

  const getDetectionBoxStyles = () => {
    if (!detection || !videoRef.current) return { display: 'none' };
    
    const box = detection.box;
    const video = videoRef.current;
    
    const top = (box.y / video.videoHeight) * 100;
    const left = (box.x / video.videoWidth) * 100;
    const width = (box.width / video.videoWidth) * 100;
    const height = (box.height / video.videoHeight) * 100;
    
    return {
      top: `${top}%`,
      left: `${left}%`,
      width: `${width}%`,
      height: `${height}%`,
      border: `2px solid ${detectionStatus === 'valid' ? theme.palette.success.main : theme.palette.warning.main}`,
      boxShadow: `0 0 10px ${detectionStatus === 'valid' ? alpha(theme.palette.success.main, 0.5) : alpha(theme.palette.warning.main, 0.5)}`,
      borderRadius: '8px',
      position: 'absolute' as 'absolute',
      zIndex: 20,
      pointerEvents: 'none' as 'none',
      transition: 'all 0.1s ease-out'
    };
  };

  return (
    <Modal show={show} onHide={onHide} size="lg" backdrop="static" keyboard={false}>
      <Modal.Header closeButton>
        <Modal.Title>{t('faceScanner')}</Modal.Title>
      </Modal.Header>
      <Modal.Body className="text-center p-0" style={{ maxHeight: '85vh', overflowY: 'auto' }}>
        {error && (
          <Alert variant="danger" className="m-3 d-flex justify-content-between align-items-center text-start">
            <div>
              <i className="bi bi-exclamation-triangle-fill me-2"></i>
              {error}
            </div>
            <Button variant="outline-danger" size="sm" onClick={() => startCamera()}>{t('retry')}</Button>
          </Alert>
        )}
        
        {!modelsLoaded && !error && (
          <div className="p-5 text-center">
            <Spinner animation="border" className="mb-2" />
            <div>{t('initializingFaceDetection')}</div>
          </div>
        )}

        <div className={`position-relative bg-dark ${!modelsLoaded ? 'd-none' : ''}`} style={{ minHeight: '260px', height: '45vh', maxHeight: '420px' }}>
          <video 
            ref={videoRef} 
            autoPlay 
            playsInline 
            muted 
            className="w-100 h-100" 
            style={{ objectFit: 'cover', transform: 'scaleX(-1)' }}
          />
          
          {detection && <div style={getDetectionBoxStyles()} />}

          <div className="position-absolute top-0 start-0 w-100 h-100 d-flex flex-column justify-content-between p-3" style={{ pointerEvents: 'none', zIndex: 30 }}>
            <div className="d-flex flex-column align-items-center gap-2">
              <div className="badge bg-primary fs-5">
                {currentStep < POSES.length ? t(POSES[currentStep].labelKey) : t('allDone')}
              </div>
              
              <div className="d-flex gap-2">
                {currentStep < POSES.length && (
                  <Badge bg="info">{t('rankPrefix')} {currentRank}</Badge>
                )}
                
                {detectionStatus === 'valid' && <Badge bg="success">{t('faceDetected')}</Badge>}
                {detectionStatus === 'too-small' && <Badge bg="warning">{t('moveCloser')}</Badge>}
                {detectionStatus === 'none' && <Badge bg="secondary">{t('noFaceFound')}</Badge>}
              </div>
            </div>
            
            <div className="align-self-center border border-2 border-primary rounded-circle" style={{ width: '250px', height: '300px', opacity: 0.3 }}></div>
            
            <div className="text-white bg-dark bg-opacity-50 p-2 rounded align-self-center">
              {currentStep < POSES.length ? (
                <>
                  {detectionStatus === 'valid' ? t('perfectKeepStill') : (detectionStatus === 'too-small' ? t('moveCloserInstruction') : t('alignInOval'))}
                </>
              ) : t('readyToSubmit')}
            </div>
          </div>

          {!isCameraReady && (
            <div className="position-absolute top-50 start-50 translate-middle text-white text-center w-100 px-4" style={{ zIndex: 40 }}>
              {error ? (
                <div className="bg-dark bg-opacity-75 p-4 rounded shadow">
                  <p className="mb-3">{t('cameraRequired')}</p>
                  <Button variant="primary" size="lg" onClick={() => startCamera()}>
                    {t('enableCamera')}
                  </Button>
                </div>
              ) : (
                <>
                  <Spinner animation="border" />
                  <div className="mt-2">{t('startingCamera')}</div>
                </>
              )}
            </div>
          )}
        </div>

        <div className="p-3 bg-light border-top">
          {currentStep < POSES.length && (
            <div className="mb-3 d-flex align-items-center justify-content-center gap-3 flex-wrap">
              <label className="fw-bold">{t('imageRankLabel')}</label>
              <input 
                type="number" 
                min="1" 
                max="10" 
                value={currentRank}
                onChange={(e) => setCurrentRank(Math.max(1, Math.min(10, parseInt(e.target.value) || 1)))}
                className="form-control form-control-sm"
                style={{ width: '70px', pointerEvents: 'auto' }}
              />
              {supportsCameraSwitch && (
                <Button variant="outline-primary" size="sm" onClick={switchCamera}>
                  {t('switchCamera', { facing: facingMode === 'user' ? t('facingFront') : t('facingBack') })}
                </Button>
              )}
            </div>
          )}

          <div className="d-flex justify-content-center gap-3 mb-3 overflow-auto py-2">
            {POSES.map((p, idx) => (
              <div key={idx} className="text-center">
                <div 
                  className={`rounded-circle border border-2 mb-1 d-flex flex-column align-items-center justify-content-center`} 
                  style={{ 
                    width: '50px', 
                    height: '50px', 
                    backgroundColor: idx < capturedImages.length ? theme.palette.success.main : (idx === currentStep ? theme.palette.primary.main : theme.palette.divider),
                    borderColor: idx === currentStep ? theme.palette.primary.dark : 'transparent',
                    color: idx <= capturedImages.length ? 'white' : 'inherit',
                    fontSize: '12px'
                  }}
                >
                  {idx < capturedImages.length ? '✓' : idx + 1}
                  <div style={{ fontSize: '8px', marginTop: '-2px' }}>
                    R{idx < capturedImages.length ? capturedImages[idx].image_rank : (idx === currentStep ? currentRank : startingRank + p.offset)}
                  </div>
                </div>
                <small style={{ fontSize: '10px' }}>{p.pose === 'front' ? t('poseFront') : (p.pose === 'left-side' ? t('poseLeft') : t('poseRight'))}</small>
              </div>
            ))}
          </div>

          <div className="d-flex justify-content-center gap-2">
            <Button variant="secondary" onClick={onHide}>{t('cancel')}</Button>
            {capturedImages.length > 0 && currentStep < POSES.length && (
              <Button variant="outline-warning" onClick={retakeLast}>{t('retakeLast')}</Button>
            )}
            {currentStep < POSES.length ? (
              <Button 
                variant="primary" 
                size="lg" 
                onClick={captureFrame} 
                disabled={!isCameraReady || !modelsLoaded || detectionStatus !== 'valid'}
              >
                {detectionStatus === 'valid' ? t('captureFrame', { rank: currentRank }) : t('waitingForFace')}
              </Button>
            ) : (
              <Button variant="success" size="lg" onClick={handleFinish}>
                {t('finishAndSave')}
              </Button>
            )}
          </div>
        </div>
        
        <canvas ref={canvasRef} style={{ display: 'none' }} />
      </Modal.Body>
    </Modal>
  );
};

export default FaceCapture;
